import { useState, useCallback, useEffect, useRef } from 'react';
import { classService } from '@/school-management/services/class';
import { GroupReportItem } from '@/school-management/types/response';
import { FilterValue } from '@/components/school-management/BimloaFilter/BimloaFilter';
import {
  buildGroupsReportQueryParams,
  CLASSES_REPORT_PAGE_SIZE,
  ClassFiltersRecord,
} from './buildGroupsReportQueryParams';

export function useGroupsReportClasses(options: {
  organizationId: string;
  classFilters?: Record<string, FilterValue>;
  searchQuery?: string;
  translateForGradeMapper: (key: string) => string;
  /** Admin "view classes for teacher" uses classFilters; teacher dashboard passes lockedTeacherIds only. */
  lockedTeacherIds?: string[];
  extraGradeIds?: number[];
  enabled?: boolean;
  failedToFetchMessage: string;
}) {
  const {
    organizationId,
    classFilters,
    searchQuery,
    translateForGradeMapper,
    lockedTeacherIds,
    extraGradeIds,
    enabled = true,
    failedToFetchMessage,
  } = options;

  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [isLoadingClasses, setIsLoadingClasses] = useState(true);
  const [isFirstLoading, setIsFirstLoading] = useState(true);
  const [classesError, setClassesError] = useState<string | null>(null);
  const [fetchedClasses, setFetchedClasses] = useState<GroupReportItem[]>([]);
  const [paginationInfo, setPaginationInfo] = useState<{
    totalPages: number;
    totalCount: number;
  }>({
    totalPages: 1,
    totalCount: 0,
  });

  const buildParams = useCallback(
    (page: number) =>
      buildGroupsReportQueryParams({
        page,
        pageSize: CLASSES_REPORT_PAGE_SIZE,
        sortByUi: sortBy,
        sortDirection,
        searchQuery,
        classFilters: classFilters as ClassFiltersRecord,
        lockedTeacherIds,
        extraGradeIds,
        translateForGradeMapper,
      }),
    [
      classFilters,
      lockedTeacherIds,
      extraGradeIds,
      searchQuery,
      sortBy,
      sortDirection,
      translateForGradeMapper,
    ]
  );

  const fetchClasses = useCallback(
    async (page: number) => {
      if (!organizationId || !enabled) {
        setIsLoadingClasses(false);
        setIsFirstLoading(false);
        return;
      }

      setIsLoadingClasses(true);
      setClassesError(null);
      try {
        const queryParams = buildParams(page);
        const response = await classService.getAllClasses(organizationId, queryParams);
        setFetchedClasses(response.data.items);
        setPaginationInfo({
          totalPages: response.data.totalPages,
          totalCount: response.data.totalCount,
        });
      } catch (error: unknown) {
        const message = (error as { message?: string })?.message || failedToFetchMessage;
        setClassesError(message);
        setFetchedClasses([]);
      } finally {
        setIsLoadingClasses(false);
        setIsFirstLoading(false);
      }
    },
    [organizationId, enabled, buildParams, failedToFetchMessage]
  );

  const lastFetchedKeyRef = useRef('');
  useEffect(() => {
    if (!organizationId || !enabled) return;
    const key = `${currentPage}-${organizationId}-${sortBy}-${sortDirection}-${searchQuery || ''}-${JSON.stringify(classFilters ?? {})}-${JSON.stringify(lockedTeacherIds ?? [])}-${JSON.stringify(extraGradeIds ?? [])}`;
    if (lastFetchedKeyRef.current === key) return;
    lastFetchedKeyRef.current = key;
    fetchClasses(currentPage);
  }, [
    currentPage,
    organizationId,
    classFilters,
    lockedTeacherIds,
    fetchClasses,
    sortBy,
    sortDirection,
    enabled,
    extraGradeIds,
    searchQuery,
  ]);

  const prevFiltersJsonRef = useRef<string | null>(null);
  useEffect(() => {
    const filtersJson = `${searchQuery || ''}-${JSON.stringify(classFilters ?? {})}`;
    if (prevFiltersJsonRef.current !== null && prevFiltersJsonRef.current !== filtersJson) {
      setCurrentPage(1);
    }
    prevFiltersJsonRef.current = filtersJson;
  }, [classFilters, searchQuery]);

  const prevExtraGradesJsonRef = useRef<string | null>(null);
  useEffect(() => {
    const json = JSON.stringify(extraGradeIds ?? []);
    if (prevExtraGradesJsonRef.current !== null && prevExtraGradesJsonRef.current !== json) {
      setCurrentPage(1);
    }
    prevExtraGradesJsonRef.current = json;
  }, [extraGradeIds]);

  const onSortChange = useCallback(
    (newSortBy: string | null, newSortDirection: 'asc' | 'desc' | null) => {
      if (newSortBy && newSortDirection) {
        setSortBy(newSortBy);
        setSortDirection(newSortDirection);
      }
    },
    []
  );

  return {
    currentPage,
    setCurrentPage,
    sortBy,
    sortDirection,
    onSortChange,
    isLoadingClasses,
    isFirstLoading,
    classesError,
    setClassesError,
    fetchedClasses,
    paginationInfo,
    refetch: fetchClasses,
    buildClassQueryParams: buildParams,
  };
}
